import { CoreLayoutView, TCoreOptions, useThemeHook } from '@core/widgets';
import { eventBus } from '@libs/core';
import { SystemEventTypeEnum, TCommonData, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import Dash from '../../common/Dash';
import { useDetectKeyboard } from '../../hooks/useDetectKeyboard';
import { SemanticColor, semanticColorsV1 } from '../../theme/colors';
import { EWidgetVersion } from '../../types/enums';
import { TLayoutOption } from '../../types/options';
import { isMobileByTouch } from '../../utils/helpers';
import { getInlineStyles } from '../../utils/styles';
import { resolveCssColor } from '../Image/imageUtils';
import styles from './css/separateLayout.module.css';
import { SeparateLayoutSheetContext, useSheetFocusSync } from './separateLayoutSheet';

type TSeparateLayoutProps = {
    topWidget: React.ReactNode;
    bottomWidget: React.ReactNode;
} & TWidgetProps<TCommonData, TLayoutOption>;

type TSeparateContentLayoutPalette = {
    statusBarColor?: string;
    expandedShadowColor?: string;
    expandedHeight?: number;
    backgroundColor?: string;
} & TCoreOptions;

const BOTTOM_MOVE = 'bottom-move';

/** v1 design of SeparateContentLayout. Rendered for widgets without `version: 2`. */
const SeparateContentLayoutV1 = ({ widget, topWidget, bottomWidget }: TSeparateLayoutProps) => {
    const { isKeyboardOpen, setIsKeyboardOpen } = useDetectKeyboard();
    const [classBottom, setClassBottom] = useState('');
    const [openedBottomLayout, setOpenedBottomLayout] = useState(false);
    const [overlayShouldBeClosed, setOverlayShouldBeClosed] = useState(false);
    const [topPosition, setTopPosition] = useState<number>();
    // animationInProgress: 0 - no animation, 1 - opening, -1 - closing
    const [animationInProgress, setAnimationInProgress] = useState(0);
    const ref = useRef<HTMLDivElement | null>(null);
    const refStartAnimation = useRef(false);
    const isUserAction = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scrollValueForClose = isMobileByTouch() ? 0.5 : 0;
    const scrollStep = isMobileByTouch() ? 0.7 : 0.5;
    const parentStyleNames = [widget.options?.style ?? ' '].filter(Boolean) as string[];
    const theme = useThemeHook<TSeparateContentLayoutPalette>(widget);

    const backgroundImage = widget.options?.backgroundImage;
    const backgroundColor = widget.options?.backgroundColor;

    const EXPANDED_HEIGHT = theme.palette?.expandedHeight ?? 65;
    const EXPANDED_HEIGHT_FOR_INNER = EXPANDED_HEIGHT - 5;

    useLayoutEffect(() => {
        const resolvedBackgroundColor = resolveCssColor(backgroundColor, EWidgetVersion.V1);
        if (resolvedBackgroundColor) {
            document.body.style.backgroundColor = resolvedBackgroundColor;
        } else {
            document.body.style.backgroundColor = semanticColorsV1[SemanticColor.BgSeparateContentLayoutTop];
        }
        const upContainer = document?.getElementById('top-container');
        const rect = upContainer?.getBoundingClientRect();
        const bottomLayoutPosition = rect?.bottom === 0 ? EXPANDED_HEIGHT : rect?.bottom;

        requestAnimationFrame(() => {
            setTopPosition(bottomLayoutPosition);
        });

        return () => {
            document.body.style.backgroundColor = '';
        };
    }, [EXPANDED_HEIGHT, backgroundColor, backgroundImage]);

    const openBottomLayout = useCallback(() => {
        refStartAnimation.current = true;
        if (animationInProgress === 0) {
            setOpenedBottomLayout(true);
            setOverlayShouldBeClosed(true);
        }
        setAnimationInProgress(1);
        setClassBottom(BOTTOM_MOVE);
        window.scrollTo(0, 0);
        eventBus.emit(SystemEventTypeEnum.SIZE, widget.internalId, 'fullScreen');
    }, [animationInProgress, widget.internalId]);

    const openOnFocus = () => {
        if (openedBottomLayout || refStartAnimation.current) {
            return false;
        }
        // Like a user action: the sheet must not be collapsed back before the keyboard is detected.
        isUserAction.current = true;
        openBottomLayout();
        return true;
    };

    const { sheet, focusTarget, focusTargetOnTouchEnd, onTouchStart, onTouchEnd, onTouchCancel, blurFocused } = useSheetFocusSync(ref, openOnFocus);

    const closeBottomLayout = useCallback(() => {
        refStartAnimation.current = true;
        blurFocused();
        setClassBottom('');
        setAnimationInProgress(-1);
        setOpenedBottomLayout(false);
        if (ref.current) {
            ref.current.scrollTop = 0;
        }
        setIsKeyboardOpen(false);
        eventBus.emit(SystemEventTypeEnum.SIZE, widget.internalId, 'notFullScreen');
    }, [blurFocused, setIsKeyboardOpen, widget.internalId]);

    const resetScroll = () => {
        if (ref.current) {
            ref.current.scrollTop = scrollStep;
        }
    };

    const onScroll = (_e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (!refStartAnimation.current) {
            isUserAction.current = true;
            if (!openedBottomLayout) {
                openBottomLayout();
                focusTargetOnTouchEnd();
                return;
            }
            if (ref?.current && ref.current?.scrollTop <= scrollValueForClose) {
                if (overlayShouldBeClosed || ref.current?.scrollTop === 0) {
                    closeBottomLayout();
                    return;
                }
                resetScroll();
                setOverlayShouldBeClosed(true);
            } else {
                if (ref.current && ref.current.scrollTop > scrollStep) {
                    setOverlayShouldBeClosed(false);
                }
            }
        }
    };

    const onDashTouch = () => {
        isUserAction.current = true;
        if (openedBottomLayout) {
            closeBottomLayout();
        } else {
            openBottomLayout();
            focusTarget();
        }
    };

    const handleClickOutside = () => {
        if (animationInProgress === 0) {
            closeBottomLayout();
        }
    };

    const onKeyboardToggle = useEffectEvent(() => {
        if (!isUserAction.current) {
            if (isKeyboardOpen && !openedBottomLayout) {
                openBottomLayout();
            } else if (!isKeyboardOpen && openedBottomLayout) {
                closeBottomLayout();
            }
        }
    });

    useEffect(() => {
        onKeyboardToggle();
    }, [isKeyboardOpen, openedBottomLayout]);

    const classNamesBottomInner = clsx(
        styles.separateLayoutBottomInner,
        animationInProgress !== 0 && openedBottomLayout
            ? styles.animationExpandCollapseLayout
            : animationInProgress !== 0 && !openedBottomLayout
              ? styles.animationCollapseExpandLayout
              : '',
    );
    const styleBottom = {
        '--bottom-top': (classBottom === BOTTOM_MOVE ? EXPANDED_HEIGHT : topPosition) + 'px',
        '--bottom-overscroll-behavior': classBottom === BOTTOM_MOVE ? 'contain' : 'auto',
        '--bottom-overflowY': animationInProgress === 0 ? 'auto' : 'hidden',
    } as React.CSSProperties;

    const styleBottomInner = {
        '--bottomInner-min-height': (openedBottomLayout ? EXPANDED_HEIGHT_FOR_INNER : topPosition) + 'px',
        '--bottomInner-top': `${topPosition}px`,
    } as React.CSSProperties;

    return (
        <div
            className={styles.separateLayout}
            data-test={widget.uid}
            style={{
                ...getInlineStyles({
                    variables: {
                        expandedShadowColor: theme.palette?.expandedShadowColor ?? 'var(--black_48_tr_base)',
                    },
                }),
                backgroundColor: theme.palette?.backgroundColor,
            }}
        >
            <div
                className={clsx(styles.separateLayoutOverlay, (animationInProgress === 1 || openedBottomLayout) && styles.overlay)}
                onClick={handleClickOutside}
            />

            <div id="top-container">{topWidget}</div>
            <CoreLayoutView
                id="bottom-container"
                ref={ref}
                className={styles.separateLayoutBottom}
                style={styleBottom}
                styleName="separateLayoutSheet"
                parentStyleNames={parentStyleNames}
                styleProps={{ borderRadius: 12, padding: { bottom: 96 } }}
                onScroll={onScroll}
                onTransitionEnd={() => {
                    setAnimationInProgress(0);
                    refStartAnimation.current = false;
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(() => {
                        isUserAction.current = false;
                    }, 100);
                }}
            >
                <div
                    className={classNamesBottomInner}
                    style={styleBottomInner}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchCancel}
                >
                    <Dash onTouchEnd={onDashTouch} styleName={'separateLayoutHandle'} parentStyleNames={parentStyleNames} />
                    <SeparateLayoutSheetContext value={sheet}>{bottomWidget}</SeparateLayoutSheetContext>
                </div>
            </CoreLayoutView>
        </div>
    );
};

export { SeparateContentLayoutV1 };
